import { STATS_CARD_ORDER, STATS_DEFAULT_ACTIVE_CARDS } from '@/config/demoConfig'
import type { StoreCosts } from './stats'

/**
 * The card catalogue, the visibility rules and the order — pure (Phase 1I).
 *
 * Every value here is a PLAIN value. In the Vue version two of them were nested
 * `computed()` objects, which is why `getMainValue` carries a ref-unwrapping
 * branch and why `calculateDifference` produced `NaN` for Total Demand; that
 * branch can die with the Vue component.
 */

export type CardType = 'currency' | 'percent' | 'number' | 'risk'

export type StatCard = {
  id: string
  label: string
  value: number | null
  icon: string
  color: string
  type: CardType
  subtitle?: string
}

/** Total Demand's number and the unit it is ACTUALLY in, decided together. */
export type DemandDisplay = { value: number; unit: string | null }

/** Exactly the metrics the catalogue reads — a subset of `computeStats`'s result. */
export type CardStats = {
  totalFacilities: number
  plantCount: number
  dcCount: number
  totalCustomers: number
  utilizationPct: number
  spareCapacity: number
  openDCs: number
  serviceLevel: number
  demandFulfilled: number
  networkRisk: number | null
  dataQuality: number | null
  baselineDeltaValue: number
}

/** What the catalogue needs: the metrics ALREADY derived, plus the rest. */
export type CardInputs = {
  stats: CardStats
  storeCosts: StoreCosts
  designResult: { vss?: number | null; vss_percent?: number | null } | null
  demandDisplay: DemandDisplay
  capacityCalculationMethod: string
  serviceLevelCalculationMethod: string
}

/**
 * Total Demand's number and its unit.
 *
 * When there is no conversion path `convert` returns null and the number stays
 * in the base unit — so the unit reported is the base unit too, not the one the
 * user selected. Saying otherwise is how the card lied.
 */
export function demandDisplayFor(
  baseValue: number,
  selectedUnit: string | null,
  baseUnit: string | null,
  convert: ((value: number, from: string, to: string) => number | null) | null,
): DemandDisplay {
  const asBase: DemandDisplay = { value: Math.round(baseValue || 0), unit: baseUnit || null }
  if (!baseUnit || !selectedUnit || selectedUnit === baseUnit || !convert) return asBase
  try {
    const converted = convert(baseValue, baseUnit, selectedUnit)
    if (converted === null || converted === undefined) return asBase
    return { value: Math.round(converted), unit: selectedUnit }
  } catch (error) {
    console.warn('[totalDemand] conversion threw; showing the base unit', error)
    return asBase
  }
}

const capitalise = (unit: string) => `${unit.charAt(0).toUpperCase()}${unit.slice(1)}`

/** Every card the bar can render, by id. */
export function buildCards(inputs: CardInputs): Record<string, StatCard> {
  const { stats: s, storeCosts, designResult, demandDisplay } = inputs
  // Absent until a design run has happened — rendered as a dash, never as zero,
  // because "$0" says uncertainty costs you nothing while null says nobody
  // checked.
  const vss = designResult && typeof designResult.vss === 'number' ? designResult.vss : null

  return {
    valueOfStochasticSolution: {
      id: 'valueOfStochasticSolution',
      label: 'Cost of Averaging',
      value: vss,
      icon: 'mdi-chart-bell-curve',
      color: 'deep-purple',
      type: 'currency',
      // The subtitle carries the state, because the number alone is ambiguous in
      // both directions: a dash could mean "not run" or "failed", and a genuine
      // $0 could mean "no penalty to averaging" or "nobody checked".
      subtitle:
        vss === null
          ? 'Not yet run'
          : vss === 0
            ? 'Same footprint either way'
            : typeof designResult?.vss_percent === 'number'
              ? `${designResult.vss_percent.toFixed(1)}% of expected cost`
              : 'vs. optimising for average demand',
    },
    totalCost: { id: 'totalCost', label: 'Total Cost', value: storeCosts.total_cost, icon: 'mdi-cash-multiple', color: 'primary', type: 'currency' },
    fixedCost: { id: 'fixedCost', label: 'Fixed Cost', value: storeCosts.fixed_cost, icon: 'mdi-warehouse', color: 'green', type: 'currency' },
    transportCost: { id: 'transportCost', label: 'Transport Cost', value: storeCosts.shipping_cost, icon: 'mdi-truck', color: 'blue', type: 'currency' },
    variableCost: { id: 'variableCost', label: 'Variable Cost', value: storeCosts.variable_cost, icon: 'mdi-factory', color: 'orange', type: 'currency' },
    holdingCost: {
      id: 'holdingCost', label: 'Holding Cost', value: storeCosts.holding_cost ?? null,
      icon: 'mdi-package-variant-closed', color: 'brown', type: 'currency', subtitle: 'Safety stock / inventory',
    },
    capacityUtil: {
      id: 'capacityUtil', label: 'Capacity Utilization', value: s.utilizationPct, icon: 'mdi-gauge',
      color: s.utilizationPct < 70 ? 'success' : s.utilizationPct < 90 ? 'warning' : 'error',
      type: 'percent',
      subtitle: inputs.capacityCalculationMethod === 'average' ? 'Avg across facilities' : 'Total network efficiency',
    },
    totalFacilities: {
      id: 'totalFacilities', label: 'Total Facilities', value: s.totalFacilities, icon: 'mdi-sitemap',
      color: 'purple', type: 'number', subtitle: `${s.plantCount} Plants, ${s.dcCount} DCs`,
    },
    openDCs: { id: 'openDCs', label: 'Open DCs', value: s.openDCs, icon: 'mdi-warehouse-check', color: 'success', type: 'number', subtitle: 'After optimization' },
    serviceLevel: {
      id: 'serviceLevel', label: 'Service Level', value: s.serviceLevel, icon: 'mdi-speedometer',
      color: s.serviceLevel >= 95 ? 'success' : 'warning', type: 'percent',
      subtitle: inputs.serviceLevelCalculationMethod === 'volume_weighted' ? 'Volume-weighted avg' : 'Per customer avg',
    },
    // Both scores are null whenever the backend did not send them, which today
    // is always. The colour must then be neutral and the subtitle must say so:
    // a green "Low risk" derived from a missing number is worse than no card,
    // because it answers a question nobody measured.
    networkRisk: {
      id: 'networkRisk', label: 'Network Risk', value: s.networkRisk, icon: 'mdi-shield-alert',
      color: s.networkRisk === null ? 'grey' : s.networkRisk <= 2.0 ? 'success' : s.networkRisk <= 3.5 ? 'warning' : 'error',
      type: 'risk',
      subtitle: s.networkRisk === null
        ? 'Not scored'
        : s.networkRisk <= 2.0 ? 'Low risk' : s.networkRisk <= 3.5 ? 'Medium risk' : 'High risk',
    },
    dataQuality: {
      id: 'dataQuality', label: 'Data Quality', value: s.dataQuality, icon: 'mdi-check-circle-outline',
      color: s.dataQuality === null ? 'grey' : s.dataQuality >= 90 ? 'success' : s.dataQuality >= 75 ? 'warning' : 'error',
      type: 'percent',
      subtitle: s.dataQuality === null
        ? 'Not scored'
        : s.dataQuality >= 90 ? 'Ready to optimize' : s.dataQuality >= 75 ? 'Minor issues' : 'Critical issues',
    },
    demandFulfilled: {
      id: 'demandFulfilled', label: 'Demand Fulfilled', value: s.demandFulfilled, icon: 'mdi-check-circle',
      color: s.demandFulfilled >= 100 ? 'success' : 'warning', type: 'percent', subtitle: 'Unmet demand',
    },
    totalCustomers: { id: 'totalCustomers', label: 'Total Customers', value: s.totalCustomers, icon: 'mdi-account-group', color: 'info', type: 'number' },
    totalDemand: {
      id: 'totalDemand', label: 'Total Demand', value: demandDisplay.value, icon: 'mdi-package-variant',
      color: 'orange', type: 'number',
      // Names the unit the NUMBER is in, which is not always the one selected.
      subtitle: demandDisplay.unit ? `${capitalise(demandDisplay.unit)} per period` : 'Units per period',
    },
    spareCapacity: {
      id: 'spareCapacity', label: 'Spare Capacity', value: s.spareCapacity, icon: 'mdi-package-variant-closed',
      color: s.spareCapacity > 0 ? 'success' : 'error', type: 'number', subtitle: 'Available headroom',
    },
    baselineDelta: {
      id: 'baselineDelta', label: 'Baseline Delta', value: s.baselineDeltaValue, icon: 'mdi-delta',
      color: 'info', type: 'currency', subtitle: 'vs baseline scenario',
    },
  }
}

/**
 * Is this card shown?
 *
 * The rule is not obvious and has shipped a bug: an id absent from BOTH the
 * saved settings and the defaults is VISIBLE, which is how Holding Cost first
 * appeared in the bar. Every new card must be listed in
 * STATS_DEFAULT_ACTIVE_CARDS, including the ones meant to stay hidden.
 */
export function isCardActive(configured: boolean | undefined, defaultValue: boolean | undefined): boolean {
  return configured === undefined ? defaultValue !== false : configured === true
}

/** The ids to show, in display order: STATS_CARD_ORDER first, then the rest alphabetically. */
export function orderedActiveCardIds(
  availableIds: string[],
  activeCards: Record<string, boolean> | null | undefined,
): string[] {
  const active = availableIds.filter((id) =>
    isCardActive(activeCards?.[id], (STATS_DEFAULT_ACTIVE_CARDS as Record<string, boolean>)[id]),
  )
  return [
    ...STATS_CARD_ORDER.filter((id: string) => active.includes(id)),
    ...active.filter((id) => !STATS_CARD_ORDER.includes(id)).sort(),
  ]
}

/** The cards to render, in order. */
export function activeCards(
  cards: Record<string, StatCard>,
  saved: Record<string, boolean> | null | undefined,
): StatCard[] {
  return orderedActiveCardIds(Object.keys(cards), saved)
    .map((id) => cards[id])
    .filter(Boolean)
}
